import { useState, useMemo, useEffect } from 'react';
import {
  Modal, Steps, Button, Calendar, Radio, Tag, Typography, Divider, message
} from 'antd';
import {
  CalendarOutlined, ClockCircleOutlined, TeamOutlined,
  DollarOutlined, CheckCircleOutlined, HomeOutlined,
  ArrowLeftOutlined, ArrowRightOutlined,
  FileDoneOutlined, PlusOutlined, MinusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import './BookingModal.css';

const { Text, Title } = Typography;

const IMG_BASE = process.env.REACT_APP_URL;

const formatPrice = (price) => {
  if (!price && price !== 0) return '0';
  return Number(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export default function BookingModal({ open, onClose, service, relatedData, onContract }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Date & Time
  const [selectedDate, setSelectedDate] = useState(null);
  const [startHour, setStartHour] = useState(null);
  const [endHour, setEndHour] = useState(null);

  // Step 2: Tariff (service_tariffs)
  const [selectedTariff, setSelectedTariff] = useState(null);

  // Step 3: Guest count (restaurant only)
  const [guestCount, setGuestCount] = useState(100);

  // Schedule: blocked slots for next 60 days
  const [schedule, setSchedule] = useState([]);
  const [blockedMap, setBlockedMap] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isRestaurant = service?.service_type?.id === 1 || service?.vendor_types_id === 1;

  // Barcha xizmat turlari uchun yagona manba: service_tariffs
  const tariffs = useMemo(() => service?.service_tariffs || [], [service]);
  const tariffFoods = useMemo(() => selectedTariff?.foods || [], [selectedTariff]);
  const tariffHalls = useMemo(() => selectedTariff?.halls || [], [selectedTariff]);

  const tariffPrice = useMemo(() => (selectedTariff ? Number(selectedTariff.price) : 0), [selectedTariff]);
  const totalPrice = useMemo(
    () => (isRestaurant ? tariffPrice * guestCount : tariffPrice),
    [isRestaurant, tariffPrice, guestCount]
  );

  // Reset state on open + fetch schedule
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setSelectedDate(null);
      setStartHour(null);
      setEndHour(null);
      setSelectedTariff(null);
      setGuestCount(100);

      if (service?.id) {
        api.get(`/public/services/${service.id}/schedule?days=60`)
          .then(res => {
            const data = res.data?.data || [];
            setSchedule(data);
            const map = {};
            data.forEach(d => { if (d.slots && d.slots.length > 0) map[d.date] = d.slots; });
            setBlockedMap(map);
          })
          .catch(() => { setSchedule([]); setBlockedMap({}); });
      }
    }
  }, [open, service?.id]);

  // Steps definition
  const steps = useMemo(() => {
    const s = [
      { title: 'Sana va vaqt', icon: <CalendarOutlined /> },
      { title: 'Tarif tanlash', icon: <DollarOutlined /> },
    ];
    if (isRestaurant) s.push({ title: 'Mehmonlar', icon: <TeamOutlined /> });
    s.push({ title: 'Shartnoma', icon: <FileDoneOutlined /> });
    return s;
  }, [isRestaurant]);

  const isContractStep = currentStep === steps.length - 1;

  // Calendar helpers
  const cellRender = (current, info) => {
    if (info.type !== 'date') return null;
    const dateStr = current.format('YYYY-MM-DD');
    const slots = blockedMap[dateStr];
    if (slots && slots.length > 0) {
      return (
        <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 2 }}>
          {slots.slice(0, 3).map((_, i) => (
            <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#e53935' }} />
          ))}
        </div>
      );
    }
    return null;
  };

  const disabledDate = (current) => current && current < dayjs().startOf('day');

  const getBlockedHours = (dateStr) => {
    const slots = blockedMap[dateStr] || [];
    const hours = new Set();
    slots.forEach(s => {
      if (s.start != null && s.end != null) {
        for (let h = s.start; h < s.end; h++) hours.add(h);
      }
    });
    return hours;
  };

  // Validation per step
  const canGoNext = () => {
    switch (currentStep) {
      case 0: return selectedDate && startHour !== null && endHour !== null && startHour < endHour;
      case 1: return !!selectedTariff;
      case 2: return isRestaurant ? guestCount > 0 : true;
      default: return true;
    }
  };

  const handleNext = () => {
    if (!canGoNext()) { message.warning('Iltimos, barcha maydonlarni to\'ldiring'); return; }
    setCurrentStep(prev => prev + 1);
  };
  const handlePrev = () => setCurrentStep(prev => prev - 1);

  // Submit booking — har doim service_tariff_id orqali
  const handleSubmit = () => {
    if (!user) { message.warning('Buyurtma berish uchun tizimga kiring'); return; }

    const bookingData = {
      service_id: service.id,
      user_id: user.id,
      provider_id: service.business?.users_id,
      date: selectedDate,
      day: dayjs(selectedDate).date(),
      month: dayjs(selectedDate).month(),
      year: dayjs(selectedDate).year(),
      start_datetime: startHour,
      end_datetime: endHour,
      service_tariff_id: selectedTariff?.id,
      tariff_name: selectedTariff?.name,
      tariff_price: tariffPrice,
      quantity: isRestaurant ? guestCount : 1,
      price: tariffPrice,
      total_price: totalPrice,
    };

    setSubmitting(true);
    api.post('/booking', bookingData).then((res) => {
      const saved = { ...bookingData, id: res.data?.id || res.data?.data?.id };
      message.success('Buyurtma muvaffaqiyatli yaratildi!');
      onClose();
      if (onContract) onContract(saved);
    }).catch((err) => {
      message.error(err.response?.data?.message || 'Buyurtma yaratishda xatolik yuz berdi');
    }).finally(() => setSubmitting(false));
  };

  // ===== STEP RENDERS =====

  const renderStepDateTime = () => (
    <div className="booking-step-content">
      <div className="booking-step-label">
        <CalendarOutlined style={{ color: '#5F33E1' }} />
        <span>Tadbirni qaysi kuni o'tkazmoqchisiz?</span>
      </div>
      <Calendar
        fullscreen={false}
        cellRender={cellRender}
        disabledDate={disabledDate}
        value={selectedDate ? dayjs(selectedDate) : undefined}
        onChange={(val) => { setSelectedDate(val.format('YYYY-MM-DD')); setStartHour(null); setEndHour(null); }}
      />
      {selectedDate && (
        <div className="booking-selected-date">
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <Text strong>{dayjs(selectedDate).format('D-MMMM, YYYY')} — tanlandi</Text>
          {blockedMap[selectedDate] && (
            <Text type="danger" style={{ fontSize: 12, marginLeft: 8 }}>
              ({blockedMap[selectedDate].length} ta band vaqt bor)
            </Text>
          )}
        </div>
      )}

      <Divider style={{ margin: '16px 0' }} />

      <div className="booking-step-label">
        <ClockCircleOutlined style={{ color: '#5F33E1' }} />
        <span>Soat nechanchi dan nechanchigacha?</span>
      </div>
      <div className="booking-time-row">
        <div className="booking-time-pick">
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>Boshlanish</Text>
          <Radio.Group
            value={startHour}
            onChange={(e) => { setStartHour(e.target.value); if (endHour !== null && e.target.value >= endHour) setEndHour(null); }}
            className="booking-hour-grid"
          >
            {Array.from({ length: 19 }, (_, i) => i + 6).map(h => {
              const busy = selectedDate ? getBlockedHours(selectedDate).has(h) : false;
              return (
                <Radio.Button key={h} value={h} className="booking-hour-btn" disabled={busy}
                  style={busy ? { background: 'rgba(255,77,79,0.12)', color: '#ff4d4f', borderColor: 'rgba(255,77,79,0.35)', textDecoration: 'line-through' } : {}}>
                  {h}:00
                </Radio.Button>
              );
            })}
          </Radio.Group>
        </div>
        {startHour !== null && (
          <div className="booking-time-pick">
            <Text type="secondary" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>Tugash</Text>
            <Radio.Group value={endHour} onChange={(e) => setEndHour(e.target.value)} className="booking-hour-grid">
              {Array.from({ length: 24 - startHour }, (_, i) => i + startHour + 1).map(h => {
                const busy = selectedDate ? getBlockedHours(selectedDate).has(h) : false;
                return (
                  <Radio.Button key={h} value={h} className="booking-hour-btn" disabled={busy}
                    style={busy ? { background: 'rgba(255,77,79,0.12)', color: '#ff4d4f', borderColor: 'rgba(255,77,79,0.35)', textDecoration: 'line-through' } : {}}>
                    {h}:00
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </div>
        )}
      </div>
      {startHour !== null && endHour !== null && (
        <div className="booking-time-summary">
          <ClockCircleOutlined />
          <Text>Davomiylik: <Text strong>{endHour - startHour} soat</Text> ({startHour}:00 — {endHour}:00)</Text>
        </div>
      )}
    </div>
  );

  const renderStepTariff = () => (
    <div className="booking-step-content">
      <div className="booking-step-label">
        <DollarOutlined style={{ color: '#5F33E1' }} />
        <span>{isRestaurant ? 'Tarifni tanlang (narx 1 kishi uchun)' : 'Tarifni tanlang'}</span>
      </div>

      {tariffs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <DollarOutlined style={{ fontSize: 40, color: '#ccc', marginBottom: 12 }} />
          <Text type="secondary" style={{ display: 'block' }}>
            Bu xizmat uchun tariflar hali kiritilmagan. Xizmat ko'rsatuvchi bilan bog'laning.
          </Text>
        </div>
      ) : (
        <div className="booking-tariff-list">
          {tariffs.map(tf => (
            <div
              key={tf.id}
              className={`booking-tariff-card ${selectedTariff?.id === tf.id ? 'booking-tariff-active' : ''}`}
              onClick={() => setSelectedTariff(tf)}
            >
              <div className="booking-tariff-icon"><DollarOutlined /></div>
              <div className="booking-tariff-info" style={{ flex: 1 }}>
                <Text strong>{tf.name}</Text>
                {(tf.halls?.length > 0) && (
                  <div><Text type="secondary" style={{ fontSize: 12 }}>
                    <HomeOutlined /> {tf.halls.map(h => h.name).join(', ')}
                  </Text></div>
                )}
                {(tf.foods?.length > 0) && (
                  <div><Text type="secondary" style={{ fontSize: 12 }}>
                    {tf.foods.length} ta taom kiritilgan
                  </Text></div>
                )}
              </div>
              <div className="booking-tariff-price">
                <Text strong style={{ color: '#5F33E1', fontSize: 16 }}>{formatPrice(tf.price)}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{isRestaurant ? "so'm/kishi" : "so'm"}</Text>
              </div>
              {selectedTariff?.id === tf.id && <CheckCircleOutlined style={{ color: '#5F33E1', fontSize: 20 }} />}
            </div>
          ))}
        </div>
      )}

      {selectedTariff && (
        <div className="booking-price-preview">
          <Text type="secondary">Tanlangan tarif:</Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <Title level={3} style={{ margin: 0, color: '#5F33E1' }}>{formatPrice(selectedTariff.price)} so'm</Title>
            {isRestaurant && <Text type="secondary">/ kishi</Text>}
          </div>
        </div>
      )}
    </div>
  );

  const renderStepGuests = () => (
    <div className="booking-step-content">
      <div className="booking-step-label">
        <TeamOutlined style={{ color: '#5F33E1' }} />
        <span>Mehmonlar soni</span>
      </div>
      <div className="booking-guest-row">
        <Button shape="circle" icon={<MinusOutlined />} size="large"
          onClick={() => setGuestCount(Math.max(1, guestCount - 10))} />
        <Title level={2} style={{ margin: '0 24px', minWidth: 80, textAlign: 'center' }}>{guestCount}</Title>
        <Button shape="circle" icon={<PlusOutlined />} size="large"
          onClick={() => setGuestCount(guestCount + 10)} />
      </div>
      <div className="booking-guest-price">
        <Text type="secondary">Tarif narxi: {formatPrice(tariffPrice)} × {guestCount} kishi = </Text>
        <Text strong style={{ color: '#5F33E1', fontSize: 16 }}>{formatPrice(tariffPrice * guestCount)} so'm</Text>
      </div>

      {/* Tarif tarkibidagi taomlar (faqat ko'rsatish) */}
      {tariffFoods.length > 0 && (
        <>
          <Divider style={{ margin: '18px 0 12px' }} />
          <div className="booking-step-label">
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Tarifga kiritilgan taomlar</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tariffFoods.map((f, i) => (
              <Tag key={i} color="green" style={{ padding: '4px 10px', fontSize: 13 }}>
                {f.name}{f.quantity > 1 ? ` ×${f.quantity}` : ''}
              </Tag>
            ))}
          </div>
        </>
      )}

      <div className="booking-step-total" style={{ marginTop: 20 }}>
        <div className="booking-step-total-row">
          <Title level={5} style={{ margin: 0 }}>Jami</Title>
          <Title level={4} style={{ margin: 0, color: '#5F33E1' }}>{formatPrice(totalPrice)} so'm</Title>
        </div>
      </div>
    </div>
  );

  const renderStepContract = () => {
    const eventDate = selectedDate ? dayjs(selectedDate).format('D-MMMM, YYYY') : '—';
    const providerName = service?.business?.name || '—';
    const serviceName = service?.name || '—';
    const clientName = user?.fullname || user?.name || '—';
    const clientPhone = user?.phone || '—';
    const hallName = tariffHalls[0]?.name;
    const advanceAmount = Math.round(totalPrice * 0.3);
    const remainingAmount = totalPrice - advanceAmount;

    return (
      <div className="booking-contract">
        <div className="booking-contract-header">
          <h2>XIZMAT KO'RSATISH SHARTNOMASI</h2>
          <p>{serviceName} xizmati bo'yicha pudrat shartnomasi</p>
        </div>

        <div className="booking-contract-meta">
          <span>Sana: <b>{new Date().toLocaleDateString('uz-UZ')}</b></span>
          <span>Platforma: <b>Quvonchli-kun.uz</b></span>
        </div>

        <Divider style={{ margin: '12px 0', borderColor: '#D4AF37' }} />

        <p className="booking-contract-text">
          Ushbu shartnoma kelgusida <b>"Buyurtmachi"</b> deb ataluvchi <b>{clientName}</b> ({clientPhone}) hamda{' '}
          <b>"Xizmat ko'rsatuvchi"</b> deb ataluvchi <b>{providerName}</b> o'rtasida quyidagilar to'g'risida tuzildi:
        </p>

        <h3 className="booking-contract-section">I. SHARTNOMANING MAZMUNI</h3>
        <p className="booking-contract-text" style={{ textIndent: 40 }}>
          1.1. "Xizmat ko'rsatuvchi" "Buyurtmachi"ning <b>{eventDate}</b> kuni soat{' '}
          <b>{startHour}:00 — {endHour}:00</b> oralig'ida <b>{serviceName}</b> xizmatini ko'rsatadi.
        </p>
        <p className="booking-contract-text" style={{ textIndent: 40 }}>
          1.2. Tarif: <b>{selectedTariff?.name || '—'}</b>.{' '}
          {hallName && <>Zal: <b>{hallName}</b>. </>}
          {isRestaurant && <>Mehmonlar soni: <b>{guestCount} kishi</b>.</>}
        </p>

        <h3 className="booking-contract-section">II. SHARTNOMANING BAHOSI</h3>
        <table className="booking-contract-table">
          <tbody>
            {isRestaurant && (
              <>
                <tr><td className="label">Narx (kishi boshiga)</td><td>{formatPrice(tariffPrice)} so'm</td></tr>
                <tr><td className="label">Mehmonlar soni</td><td>{guestCount} kishi</td></tr>
              </>
            )}
            <tr style={{ background: '#f0f7ff' }}>
              <td className="label" style={{ fontWeight: 700, fontSize: 14 }}>Jami shartnoma summasi</td>
              <td style={{ fontWeight: 700, fontSize: 14, color: 'var(--qk-text)' }}>{formatPrice(totalPrice)} so'm</td>
            </tr>
            <tr style={{ background: 'rgba(245,158,11,0.12)' }}>
              <td className="label">Avans to'lov (30%)</td>
              <td style={{ color: '#D97706', fontWeight: 600 }}>{formatPrice(advanceAmount)} so'm</td>
            </tr>
            <tr style={{ background: 'rgba(34,197,94,0.12)' }}>
              <td className="label">Xizmatdan keyin to'lov</td>
              <td style={{ color: '#16a34a', fontWeight: 600 }}>{formatPrice(remainingAmount)} so'm</td>
            </tr>
          </tbody>
        </table>

        {tariffFoods.length > 0 && (
          <>
            <h3 className="booking-contract-section">III. TARIFGA KIRITILGAN TAOMLAR</h3>
            <p className="booking-contract-text" style={{ textIndent: 40 }}>
              {tariffFoods.map(f => `${f.name}${f.quantity > 1 ? ` (×${f.quantity})` : ''}`).join(', ')}.
            </p>
          </>
        )}

        <Divider style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />

        <div className="booking-contract-signs">
          <div className="booking-contract-sign-box">
            <p style={{ fontWeight: 700, marginBottom: 8 }}>BUYURTMACHI:</p>
            <p>{clientName}</p>
            <p>Tel: {clientPhone}</p>
            <div className="booking-contract-sign-line">Imzo / Sana</div>
          </div>
          <div className="booking-contract-sign-box">
            <p style={{ fontWeight: 700, marginBottom: 8 }}>XIZMAT KO'RSATUVCHI:</p>
            <p>{providerName}</p>
            <p>{serviceName}</p>
            <div className="booking-contract-sign-line">Imzo / Sana</div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 0) return renderStepDateTime();
    if (currentStep === 1) return renderStepTariff();
    if (isRestaurant && currentStep === 2) return renderStepGuests();
    return renderStepContract();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CalendarOutlined style={{ color: '#5F33E1', fontSize: 20 }} />
          <span>Buyurtma berish — {service?.name}</span>
        </div>
      }
      footer={
        <div className="booking-modal-footer">
          {(currentStep > 0 && selectedTariff) && (
            <div className="booking-footer-price">
              <Text type="secondary" style={{ fontSize: 12 }}>Jami:</Text>
              <Text strong style={{ fontSize: 18, color: '#5F33E1' }}>{formatPrice(totalPrice)} so'm</Text>
            </div>
          )}
          <div className="booking-footer-actions">
            {currentStep > 0 && (
              <Button icon={<ArrowLeftOutlined />} onClick={handlePrev} size="large">Orqaga</Button>
            )}
            {!isContractStep ? (
              <Button type="primary" size="large" onClick={handleNext} disabled={!canGoNext()}
                style={{ background: canGoNext() ? 'linear-gradient(135deg,#5F33E1,#8B5CF6)' : undefined, border: 'none' }}>
                Keyingisi <ArrowRightOutlined />
              </Button>
            ) : (
              <Button type="primary" size="large" loading={submitting} onClick={handleSubmit} disabled={!user}
                icon={<CheckCircleOutlined />} style={{ background: 'linear-gradient(135deg,#52c41a,#389e0d)', border: 'none' }}>
                {user ? 'Shartnomani tasdiqlash' : 'Tizimga kiring'}
              </Button>
            )}
          </div>
        </div>
      }
      styles={{ body: { padding: 0, maxHeight: '68vh', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <div className="booking-modal-body">
        <div className="booking-steps-bar">
          <Steps current={currentStep} items={steps} size="small" responsive={false} />
        </div>
        {renderCurrentStep()}
      </div>
    </Modal>
  );
}
